import { date as dateLib } from 'quasar'

export default {
  setup ({ masterdata, brandConfig, i18n }) {
    this.masterdata = masterdata
    this.brandConfig = brandConfig
    this.i18n = i18n
  },
  defaultInvoiceUnit (type) {
    switch (type) {
      case 'NATURAL_GAS':
      case 'ELECTRICITY':
      case 'ELECTRIC_HEATING':
        return 'KWH'
      default:
        return null
    }
  },
  defaultMeterUnit (type) {
    switch (type) {
      case 'NATURAL_GAS':
        return 'CBM'
      case 'ELECTRICITY':
      case 'ELECTRIC_HEATING':
        return 'KWH'
      default:
        return null
    }
  },
  formatAllDates (obj, format = 'YYYY-MM-DD') {
    Object.entries(obj)
      .forEach(([key, value]) => {
        if (value instanceof Date) {
          obj[key] = dateLib.formatDate(value, format)
        }
      })
  },
  formatDateIn (obj, dateProp, format = 'YYYY-MM-DD') {
    const date = obj?.[dateProp]
    if (!date) {
      return null
    }
    obj[dateProp] = dateLib.formatDate(date, format)
    return obj[dateProp]
  },
  formatPriceUnitValue (puv, valueProperty = 'gross') {
    if (!puv) {
      return ''
    }

    const { [valueProperty]: v, currency: c, unit: u, decimals: d } = puv

    return this.formatCurrency({ v, c, u, d })
  },
  categorizeProcessingStates (processingStates) {
    processingStates = { ...processingStates }
    delete processingStates.__typename

    const stateEntries = Object.entries(processingStates)

    const omittedStates = stateEntries.filter(([, state]) => state === null)
    const erroredStates = stateEntries.filter(([, state]) => state !== null && state.dateTime !== null && state.success === false)
    const successfulStates = stateEntries.filter(([, state]) => state !== null && state.dateTime !== null && state.success)
    const remainingStates = stateEntries.filter(([name]) => !erroredStates.find(([n]) => n === name) && !successfulStates.find(([n]) => n === name) && !omittedStates.find(([n]) => n === name))

    const hasError = erroredStates.length > 0
    const finishedSuccessfully = erroredStates.length === 0 && successfulStates.length === (stateEntries.length - omittedStates.length)
    const isRunning = !hasError && !finishedSuccessfully

    const mapState = ([name, state]) => {
      if (!state) {
        state = { omitted: true }
      }
      return { name, localizedName: this.i18n.t(`orders.state.entries.${name}`), ...state }
    }

    return {
      hasError,
      finishedSuccessfully,
      isRunning,
      omitted: omittedStates.map(mapState),
      states: stateEntries.map(mapState),
      errored: erroredStates.map(mapState),
      successful: successfulStates.map(mapState),
      remaining: remainingStates.map(mapState)
    }
  },
  orderStateData (processingStates) {
    if (!processingStates) return {}
    const categorized = this.categorizeProcessingStates(processingStates)

    if (categorized.hasError) {
      /* const errorCount = categorized.errored.length
      const firstError = categorized.errored[0] */
      return {
        icon: 'icon-bo_failed_full',
        color: 'negative',
        textColor: 'white',
        label: this.i18n.t('orders.state.error') // i18n.tc('orders.state.errored', errorCount, { firstError: firstError.localizedName, moreCount: errorCount - 1 })
      }
    } else if (categorized.isRunning) {
      /* const remainingCount = categorized.remaining.length
      const firstRemaining = categorized.remaining[0] */
      return {
        icon: 'icon-bo_pending_full',
        color: 'warning',
        textColor: 'white',
        label: this.i18n.t('orders.state.pending')// i18n.tc('orders.state.running', remainingCount, { firstRemaining: firstRemaining.localizedName, moreCount: remainingCount - 1 })
      }
    } else if (categorized.finishedSuccessfully) {
      return {
        icon: 'icon-bo_success_full',
        color: 'positive',
        textColor: 'white',
        label: this.i18n.t('orders.state.completed')
      }
    }
  },
  singleStateData (categorized, name) {
    const err = categorized.errored.find(s => s.name === name)
    if (err) {
      return { icon: 'icon-bo_failed_full', color: 'negative', label: this.i18n.t('orders.state.error'), ...err }
    }

    const success = categorized.successful.find(s => s.name === name)
    if (success) {
      return { icon: 'icon-bo_success_full', color: 'positive', label: this.i18n.t('orders.state.completed'), ...success }
    }

    const inProgress = categorized.remaining.find(s => s.name === name)
    if (inProgress) {
      return { icon: 'icon-bo_pending_full', color: 'warning', label: this.i18n.t('orders.state.pending'), ...inProgress }
    }

    const omitted = categorized.omitted.find(s => s.name === name)
    if (omitted) {
      return { icon: 'icon-bo_omitted_full', color: 'grey-7', label: this.i18n.t('orders.state.omitted'), ...omitted }
    }

    return null
  },
  formatCurrency (value, perUnitSign = '/', prependCurrency = false) {
    if (!value) {
      return '-'
    }

    const v = value.v
    let c = value.c
    let u = value.u
    const d = value.d ?? 2

    let formatted = new Intl.NumberFormat(this.i18n.locale, {
      minimumFractionDigits: d,
      maximumFractionDigits: d
    }).format(v)

    if (u && this.i18n.te('units.' + u.toLowerCase())) {
      u = this.i18n.t('units.' + u.toLowerCase())
    }

    if (c && this.i18n.te('units.' + c.toLowerCase())) {
      c = this.i18n.t('units.' + c.toLowerCase())
    }

    formatted = prependCurrency ? c + formatted : `${formatted} ${c}`

    if (u) {
      formatted += ` ${perUnitSign} ${u}`
    }

    return formatted
  },

  formatNumber (value, unit, digits = 0, decimals = 0, leadingZeros, useGrouping) {
    if (value === null || isNaN(value)) {
      return '-'
    }

    if (decimals === 0) {
      value = Math.floor(value)
    }

    const formatted = this.i18n.n(value, 'decimal', {
      minimumIntegerDigits: leadingZeros ? digits : 1,
      minimumFractionDigits: decimals,
      useGrouping
    })
    if (unit) {
      return `${formatted} ${this.unitLabel(unit)}`
    }
    return formatted
  },

  formatReading (value, unit, digits = 0, decimals = 0) {
    const leadingZeros = this.brandConfig.get('formats.readingValue.leadingZeros', false)
    const useGrouping = this.brandConfig.get('formats.readingValue.useGrouping', true)

    return this.formatNumber(value, unit, digits, decimals, leadingZeros, useGrouping)
  },

  formatAddress (value, inline = false, includeAddress = true, includeName = true) {
    if (!value) {
      return ''
    }

    let { title, firstName, lastName, businessName, businessContactPartner, nameAddition, addressAddition, street, houseNumber, zipCode, city, country, poBox } = value
    if (country?.key === 'DE' || country === 'DE') {
      country = null
    } else {
      country = this.masterdata.getMasterDataDescription('countries', country)
    }
    const lines = []
    const result = []

    if (includeName) {
      // salutation = this.masterdata.getMasterDataDescription('salutations', salutation)
      title = this.masterdata.getMasterDataDescription('titles', title?.key || title)
      lines.push([title, firstName, lastName, businessName, nameAddition]) // salutation
      lines.push([businessContactPartner])
    }
    if (includeAddress) {
      lines.push([addressAddition])
      lines.push([street, houseNumber])
      lines.push([poBox])
      lines.push([zipCode, city])
      lines.push([country])
    }

    lines.forEach(l => {
      const line = l.filter(p => p)
      if (line.length > 0) {
        result.push(line.join(' '))
      }
    })

    if (inline) {
      return result.join(', ')
    } else {
      return result.join('<br>')
    }
  },

  formatTerm (term) {
    if (!term) {
      return null
    }
    const { value, unit, reference } = term
    return (
      value + ' ' + this.i18n.tc('units.' + unit.toLowerCase(), term.value) + (reference ? ' ' + this.i18n.t('contractOverview.reference.to') + ' ' + this.i18n.t(`contractOverview.reference.${reference.toLowerCase()}`) : '')
    )
  },

  unitLabel (value) {
    return this.i18n.t(`units.${value?.toLowerCase()}`)
  },

  typeIcon (value) {
    switch (value) {
      case 'OTHER':
        return 'stop_circle'
      default:
        return 'app:energy-' + value.toLowerCase().replace('_', '-')
    }
  },

  typeBackgroundColor (value) {
    return this._getColor(value, 'background') || '#2a2a2a'
  },

  typeTextColor (value) {
    return this._getColor(value, 'foreground', false) || '#fff'
  },

  typeIndicatorColor (value) {
    return this._getColor(value, 'indicator', false) || this.typeBackgroundColor(value)
  },

  typeChartColor (value) {
    return this._getColor(value, 'chart', false) || this.typeBackgroundColor(value)
  },

  typeColor (value, expired) {
    return this.typeBackgroundColor(value)
  },

  _getColor (value, property, withPrimaryColor = true) {
    let color = this.brandConfig.get('colors.energyTypes.' + property + '.' + value?.toLowerCase())
    if (color) {
      return color
    }
    color = this.brandConfig.get('colors.energyTypes.' + property + '.default')
    if (color) {
      return color
    }
    if (withPrimaryColor) {
      color = this.brandConfig.get('primarycolor')
      if (color) {
        return color
      }
    }
    return null
  }
}
